import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth, getAccessToken, parseJwtPayload } from '@vineyard/shared'
import LoadingSpinner from './LoadingSpinner'

const PUBLIC_ROUTES = ['/', '/login', '/signup', '/auth/callback', '/privacy', '/terms', '/third-party-licenses', '/forgot-password', '/reset-password']

const PENDING_ALLOWED: string[] = [
  '/church/pending',
  '/home',
  '/board',
  '/bulletin',
  '/sermons',
  '/calendar',
  '/profile',
  '/auth/',
  '/church',
]

const PENDING_WRITE_BLOCKED: string[] = [
  '/board/new',
  '/calendar/new',
  '/sermons/new',
  '/groups/new',
]

function isPendingAllowed(pathname: string): boolean {
  if (PENDING_WRITE_BLOCKED.some((blocked) => pathname === blocked || pathname.startsWith(blocked + '/'))) {
    return false
  }
  return PENDING_ALLOWED.some((allowed) => {
    if (allowed.endsWith('/')) return pathname.startsWith(allowed)
    return pathname === allowed || pathname.startsWith(allowed + '/')
  }) && !pathname.startsWith('/church/settings')
    && !pathname.startsWith('/church/register')
    && !pathname.startsWith('/church/member-settings')
}

export default function RouteGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const pathname = location.pathname

  const isPublic = PUBLIC_ROUTES.includes(pathname)

  useEffect(() => {
    if (loading) return

    // 비로그인 사용자가 보호된 페이지 접근 → /login
    if (!user && !isPublic) {
      navigate('/login', { replace: true })
      return
    }

    // 로그인된 사용자가 공개 페이지 접근 → /home
    if (user && (pathname === '/' || pathname === '/login' || pathname === '/signup')) {
      navigate('/home', { replace: true })
      return
    }

    // pending 역할 접근 제어 — JWT payload에서 role 추출
    if (user && !isPublic) {
      const token = getAccessToken()
      const payload = token ? parseJwtPayload(token) : null
      const role = payload?.role
      if (role === 'pending' && !isPendingAllowed(pathname)) {
        navigate('/church/pending', { replace: true })
      }
    }
  }, [user, loading, pathname, isPublic, navigate])

  if (loading) return <LoadingSpinner />

  return <>{children}</>
}
